import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import api, { mediaUrl } from '@/lib/api';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import {
  Sparkles, Globe, Clock, MapPin, GraduationCap, Play, Upload,
  CheckCircle2, Wind, Camera, Volume2, VolumeX,
} from 'lucide-react';

export default function CoachPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2);
  const [coach, setCoach] = useState(null);

  useEffect(() => {
    api.get(`/coach?lang=${lang}`).then(({ data }) => setCoach(data)).catch(() => {});
  }, [lang]);

  const portrait = mediaUrl(coach?.portrait_url || '/api/static/coach/kaia_portrait.png');
  const intro = coach?.intro;
  const introReady = intro?.status === 'completed' && intro?.video_url;

  // Hero reel: autoplay muted + looping gives a lively "living portrait" feel;
  // users can turn the sound on. Set muted via ref so autoplay isn't blocked.
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  useEffect(() => {
    if (videoRef.current) { videoRef.current.muted = true; }
  }, [introReady, intro?.video_url]);
  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
  };
  const posts = coach?.posts || [
    '/api/static/coach/kaia_post1.png', '/api/static/coach/kaia_post2.png',
    '/api/static/coach/kaia_post3.png', '/api/static/coach/kaia_post4.png',
  ];

  const stats = [
    { Icon: Globe, label: t('coach.stats.languages'), val: t('coach.stats.languagesVal') },
    { Icon: Clock, label: t('coach.stats.available'), val: t('coach.stats.availableVal') },
    { Icon: MapPin, label: t('coach.stats.spots'), val: t('coach.stats.spotsVal') },
    { Icon: GraduationCap, label: t('coach.stats.levels'), val: t('coach.stats.levelsVal') },
  ];
  const skills = ['s1', 's2', 's3', 's4'];

  return (
    <div className="min-h-screen" data-testid="coach-page">
      <Seo
        title={t('coach.heroTitle', 'Maak kennis met Kaia — jouw AI-kitesurfcoach')}
        description={t('coach.bio')}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Kaia — AI Kitesurf Coach',
          serviceType: 'AI video coaching voor kitesurfers',
          description: t('coach.bio'),
          provider: { '@type': 'Organization', name: 'KITEIQ', url: 'https://kitecoach.app' },
          areaServed: ['NL', 'BE', 'DE'],
          availableLanguage: ['nl', 'en', 'de'],
          url: 'https://kitecoach.app/kaia',
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(70% 60% at 80% 0%, rgba(0,180,216,0.16), rgba(0,180,216,0) 60%)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16 grid lg:grid-cols-2 gap-10 items-center relative">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div className="label-eyebrow text-[#48CAE4] flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4" /> {t('coach.heroEyebrow')}
            </div>
            <h1 className="font-display text-6xl sm:text-7xl leading-none">{coach?.name || 'Kaia'}</h1>
            <p className="text-xl text-[#48CAE4] mt-2 font-medium">{t('coach.tagline')}</p>
            <p className="text-muted-foreground mt-5 leading-relaxed max-w-xl">{t('coach.bio')}</p>
            <p className="text-muted-foreground mt-3 leading-relaxed max-w-xl">{t('coach.bioP2')}</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Button onClick={() => navigate('/analyze')} data-testid="coach-cta-analyze"
                className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl h-12 px-6">
                <Upload className="w-4 h-4 mr-2" /> {t('coach.ctaAnalyze')}
              </Button>
              <Button onClick={() => navigate('/pricing')} data-testid="coach-cta-pricing"
                className="bg-[#FFB020] hover:bg-[#ffc04d] text-black font-semibold rounded-xl h-12 px-6">
                {t('coach.ctaPricing')}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground/70 mt-5 flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5" /> {t('coach.faceNote')}
            </p>
          </motion.div>

          {/* Hero media: Kaia's intro reel (autoplay muted loop = lively portrait), else poster */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.45 }}
            className="relative mx-auto w-full max-w-[340px]">
            <div className="absolute -inset-5 rounded-[2.4rem] bg-[#00B4D8]/15 blur-3xl opacity-70 pointer-events-none" aria-hidden="true" />
            <div className="kite-card overflow-hidden relative aspect-[9/16] rounded-[1.75rem] border border-[#00B4D8]/30 shadow-2xl" data-testid="coach-hero-media">
              {introReady ? (
                <>
                  <video ref={videoRef} src={mediaUrl(intro.video_url)} poster={portrait}
                    autoPlay muted loop playsInline preload="metadata"
                    className="w-full h-full object-cover bg-black" data-testid="coach-intro-video" />
                  <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur border border-white/15 px-2.5 py-1 text-[11px] font-medium text-white/90">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2EE59D] animate-pulse" /> {t('coach.liveBadge')}
                  </span>
                  <button onClick={toggleSound} data-testid="coach-video-sound" type="button"
                    aria-label={muted ? t('coach.unmute') : t('coach.mute')}
                    className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur border border-white/20 px-3 py-1.5 text-xs font-medium text-white hover:bg-black/80 transition-colors">
                    {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5 text-[#48CAE4]" />}
                    {muted ? t('coach.unmute') : t('coach.mute')}
                  </button>
                </>
              ) : (
                <>
                  <img src={portrait} alt="Kaia" className="w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0) 55%, rgba(10,10,10,0.8) 100%)' }} />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full bg-black/55 backdrop-blur border border-white/15 text-white/85">
                      <Play className="w-3.5 h-3.5 text-[#48CAE4]" />
                      {intro?.status === 'processing' ? t('coach.introBuilding') : t('coach.introComingSoon')}
                    </span>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map(({ Icon, label, val }) => (
            <div key={label} className="kite-card p-4 flex items-center gap-3" data-testid={`coach-stat-${label}`}>
              <div className="w-10 h-10 rounded-xl bg-[#00B4D8]/12 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#00B4D8]" />
              </div>
              <div>
                <div className="font-data text-xl font-semibold">{val}</div>
                <div className="label-eyebrow text-muted-foreground">{label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SKILLS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
        <h2 className="font-display text-3xl mb-5 flex items-center gap-2">
          <Wind className="w-6 h-6 text-[#48CAE4]" /> {t('coach.skillsTitle')}
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {skills.map((s) => (
            <div key={s} className="kite-card p-4 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#2EE59D] mt-0.5 shrink-0" />
              <span className="text-white/90">{t(`coach.skills.${s}`)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FEED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-12">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="font-display text-3xl">{t('coach.feedTitle')}</h2>
            <p className="text-muted-foreground text-sm mt-1">{t('coach.feedSubtitle')}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="coach-feed">
          {posts.map((p, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.3, delay: i * 0.05 }}
              className="kite-card overflow-hidden group">
              <div className="relative aspect-square overflow-hidden">
                <img src={mediaUrl(p)} alt="" loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0) 50%, rgba(10,10,10,0.82) 100%)' }} />
                <p className="absolute bottom-3 left-3 right-3 text-xs text-white/90 leading-snug">{t(`coach.posts.post${i + 1}`)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-20">
        <div className="rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden"
          style={{ background: 'radial-gradient(80% 120% at 50% 0%, rgba(0,180,216,0.18), rgba(0,180,216,0) 60%), #12161B', border: '1px solid rgba(0,180,216,0.25)' }}>
          <h2 className="font-display text-4xl sm:text-5xl">{t('coach.ctaTitle')}</h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">{t('coach.ctaDesc')}</p>
          <Button onClick={() => navigate('/analyze')} data-testid="coach-cta-bottom"
            className="mt-7 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl h-12 px-8">
            <Upload className="w-4 h-4 mr-2" /> {t('coach.ctaAnalyze')}
          </Button>
        </div>
      </section>
    </div>
  );
}
